import type {
  MomentButton,
  MomentCarouselItem,
  MomentCommerceIntro,
  MomentCoupon,
  MomentImageAsset,
  MomentListItem,
  MomentMessageType,
  MomentMoreButton,
} from "../data/momentOptions";
import { normalizeUrl } from "./messageBodyBuilder";

export type ValidateParams = {
  messageName: string;
  messageType: MomentMessageType;
  messageBody: string;
  messageTitle: string;
  wideImage: MomentImageAsset;
  landingMobileUrl: string;
  buttons: MomentButton[];
  couponEnabled: boolean;
  coupon: MomentCoupon;
  moreButtonEnabled: boolean;
  moreButton: MomentMoreButton;
  listItems: MomentListItem[];
  carouselItems: MomentCarouselItem[];
  commerceIntroEnabled: boolean;
  commerceIntro: MomentCommerceIntro;
};

function getCouponTitleLimit(messageType: MomentMessageType) {
  return messageType === "WIDE_MESSAGE" || messageType === "WIDE_LIST_MESSAGE" ? 12 : 7;
}

function getCouponDescriptionLimit(messageType: MomentMessageType) {
  return messageType === "WIDE_MESSAGE" || messageType === "WIDE_LIST_MESSAGE" ? 18 : 12;
}

function getMomentUrlValidationMessage(url: string): string | null {
  const normalizedUrl = normalizeUrl(url);
  if (!normalizedUrl) return null;

  if (!/^https?:\/\//i.test(normalizedUrl)) {
    return "랜딩 URL은 http:// 또는 https:// 형식만 사용할 수 있습니다.";
  }

  if (normalizedUrl.split("").some((char) => char.charCodeAt(0) > 127)) {
    return "랜딩 URL에 한글 또는 인코딩되지 않은 특수문자가 포함되어 있습니다. UTF-8 인코딩된 URL을 사용해주세요.";
  }

  if (/[|“”]/.test(normalizedUrl)) {
    return "랜딩 URL에 지원되지 않는 특수문자가 포함되어 있습니다. UTF-8 인코딩된 URL을 사용해주세요.";
  }

  if (/%(?![0-9A-Fa-f]{2})/.test(normalizedUrl)) {
    return "랜딩 URL의 % 문자는 인코딩된 형식(%HH)으로만 사용할 수 있습니다.";
  }

  try {
    // Parse once to reject malformed URLs and unsupported schemes such as deeplinks.
    const parsedUrl = new URL(normalizedUrl);
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return "딥링크 형식의 URL은 지원하지 않습니다.";
    }

    const hostname = parsedUrl.hostname?.trim() ?? "";
    if (!hostname) {
      return "랜딩 URL 형식이 올바르지 않습니다.";
    }

    const isIpv4 = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname);
    const isIpv6 = hostname.includes(":");
    const isLocalhost = hostname.toLowerCase() === "localhost";
    if (!isIpv4 && !isIpv6 && !isLocalhost && !hostname.includes(".")) {
      return "랜딩 URL의 도메인 형식이 올바르지 않습니다.";
    }
  } catch (error) {
    return "랜딩 URL 형식이 올바르지 않습니다.";
  }

  return null;
}

function validateUrlField(url: string, emptyMessage: string, invalidLabel: string): string | null {
  const normalizedUrl = normalizeUrl(url);
  if (!normalizedUrl) return emptyMessage;

  const validationMessage = getMomentUrlValidationMessage(normalizedUrl);
  if (validationMessage) {
    return `${invalidLabel} ${validationMessage}`;
  }

  return null;
}

function validateOptionalUrlField(url: string, invalidLabel: string): string | null {
  const normalizedUrl = normalizeUrl(url);
  if (!normalizedUrl) return null;

  const validationMessage = getMomentUrlValidationMessage(normalizedUrl);
  if (validationMessage) {
    return `${invalidLabel} ${validationMessage}`;
  }

  return null;
}

export function validateBeforeSave(params: ValidateParams): string | null {
  const {
    messageName, messageType, messageBody, messageTitle, wideImage,
    landingMobileUrl, couponEnabled, coupon, moreButtonEnabled, moreButton,
    listItems, carouselItems, commerceIntroEnabled, commerceIntro,
  } = params;

  if (!messageName.trim()) return "메시지 이름을 입력해주세요.";

  if (messageType === "BASIC_TEXT_MESSAGE" && !messageBody.trim()) {
    return "홍보문구를 입력해주세요.";
  }
  if (messageType === "BASIC_TEXT_MESSAGE" && messageBody.length > 1300) {
    return "홍보문구는 최대 1300자까지 입력할 수 있습니다.";
  }
  if (messageType === "BASIC_TEXT_MESSAGE" && (messageBody.match(/\n/g) || []).length > 99) {
    return "홍보문구는 개행을 최대 99개까지 입력할 수 있습니다.";
  }

  // BASIC_TEXT_MESSAGE / WIDE_MESSAGE 공통: 버튼 URL 검증
  if (messageType === "BASIC_TEXT_MESSAGE" || messageType === "WIDE_MESSAGE") {
    const { buttons } = params;
    for (let i = 0; i < buttons.length; i += 1) {
      const btn = buttons[i];
      if (!btn.title.trim()) continue; // 빈 버튼은 buildMessageBody에서 필터됨
      if (btn.title.length > 8) return `버튼 ${i + 1} 이름은 최대 8자까지 입력할 수 있습니다.`;
      const btnMobileValidation = validateUrlField(
        btn.mobileLandingUrl,
        `버튼 ${i + 1} 모바일 랜딩 URL을 입력해주세요.`,
        `버튼 ${i + 1} 모바일 랜딩 URL은`
      );
      if (btnMobileValidation) return btnMobileValidation;
      const btnPcValidation = validateOptionalUrlField(
        btn.pcLandingUrl,
        `버튼 ${i + 1} PC 랜딩 URL은`
      );
      if (btnPcValidation) return btnPcValidation;
    }
  }

  if (messageType === "WIDE_MESSAGE") {
    if (!wideImage.imageFile && !wideImage.imageUploadedUrl) return "와이드 이미지를 등록해주세요.";
    const wideLandingValidationMessage = validateUrlField(
      landingMobileUrl,
      "와이드 이미지 모바일 랜딩 URL을 입력해주세요.",
      "와이드 이미지 모바일 랜딩 URL은"
    );
    if (wideLandingValidationMessage) return wideLandingValidationMessage;
    if (!messageBody.trim()) return "홍보문구를 입력해주세요.";
    if (messageBody.length > 76) return "홍보문구는 최대 76자까지 입력할 수 있습니다.";
    if ((messageBody.match(/\n/g) || []).length > 5) return "홍보문구는 개행을 최대 5개까지 입력할 수 있습니다.";
  }

  if (messageType === "WIDE_LIST_MESSAGE") {
    if (!messageTitle.trim()) return "와이드 리스트 타이틀을 입력해주세요.";
    if (messageTitle.length > 20) return "와이드 리스트 타이틀은 최대 20자까지 입력할 수 있습니다.";
    if ((messageTitle.match(/\n/g) || []).length > 0) return "와이드 리스트 타이틀은 개행 없이 입력해주세요.";
    if (listItems.length < 3) return "와이드 리스트는 최소 3개 이상 등록해주세요.";
    for (let i = 0; i < listItems.length; i += 1) {
      const item = listItems[i];
      if (!item.description.trim()) return `리스트 ${i + 1} 홍보문구를 입력해주세요.`;
      const descLimit = i === 0 ? 25 : 30;
      if (item.description.length > descLimit) return `리스트 ${i + 1} 홍보문구는 최대 ${descLimit}자까지 입력할 수 있습니다.`;
      if (!item.imageFile && !item.imageUploadedUrl) return `리스트 ${i + 1} 이미지를 등록해주세요.`;
      const itemMobileLandingValidationMessage = validateUrlField(
        item.mobileLandingUrl,
        `리스트 ${i + 1} 모바일 랜딩 URL을 입력해주세요.`,
        `리스트 ${i + 1} 모바일 랜딩 URL은`
      );
      if (itemMobileLandingValidationMessage) return itemMobileLandingValidationMessage;
      const itemPcLandingValidationMessage = validateOptionalUrlField(
        item.pcLandingUrl,
        `리스트 ${i + 1} PC 랜딩 URL은`
      );
      if (itemPcLandingValidationMessage) return itemPcLandingValidationMessage;
    }
  }

  if (messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE") {
    if (moreButtonEnabled) {
      const moreButtonMobileLandingValidationMessage = validateUrlField(
        moreButton.mobileLandingUrl,
        "더보기 모바일 랜딩 URL을 입력해주세요.",
        "더보기 모바일 랜딩 URL은"
      );
      if (moreButtonMobileLandingValidationMessage) return moreButtonMobileLandingValidationMessage;
      const moreButtonPcLandingValidationMessage = validateOptionalUrlField(
        moreButton.pcLandingUrl,
        "더보기 PC 랜딩 URL은"
      );
      if (moreButtonPcLandingValidationMessage) return moreButtonPcLandingValidationMessage;
    }
    if (messageType === "CAROUSEL_FEED_MESSAGE" && carouselItems.length < 2) {
      return "피드 캐러셀 카드는 최소 2개 이상 등록해주세요.";
    }
    if (messageType === "CAROUSEL_COMMERCE_MESSAGE") {
      if (commerceIntroEnabled) {
        if (!commerceIntro.messageTitle.trim()) return "인트로 카드 타이틀을 입력해주세요.";
        if ((commerceIntro.messageTitle.match(/\n/g) || []).length > 0) return "인트로 카드 타이틀은 개행 없이 입력해주세요.";
        if (commerceIntro.messageTitle.length > 20) return "인트로 카드 타이틀은 최대 20자까지 입력할 수 있습니다.";
        if (!commerceIntro.description.trim()) return "인트로 카드 홍보문구를 입력해주세요.";
        if ((commerceIntro.description.match(/\n/g) || []).length > 0) return "인트로 카드 홍보문구는 개행 없이 입력해주세요.";
        if (commerceIntro.description.length > 50) return "인트로 카드 홍보문구는 최대 50자까지 입력할 수 있습니다.";
        if (!commerceIntro.imageFile && !commerceIntro.imageUploadedUrl) return "인트로 카드를 위한 이미지를 등록해주세요.";
        const introMobileLandingValidationMessage = validateUrlField(
          commerceIntro.mobileLandingUrl,
          "인트로 카드 모바일 랜딩 URL을 입력해주세요.",
          "인트로 카드 모바일 랜딩 URL은"
        );
        if (introMobileLandingValidationMessage) return introMobileLandingValidationMessage;
        const introPcLandingValidationMessage = validateOptionalUrlField(
          commerceIntro.pcLandingUrl,
          "인트로 카드 PC 랜딩 URL은"
        );
        if (introPcLandingValidationMessage) return introPcLandingValidationMessage;
      }
      if (carouselItems.length < (commerceIntroEnabled ? 1 : 2)) {
        return commerceIntroEnabled
          ? "커머스 캐러셀 카드는 최소 1개 이상 등록해주세요."
          : "커머스 캐러셀 카드는 최소 2개 이상 등록해주세요.";
      }
    }
    for (let i = 0; i < carouselItems.length; i += 1) {
      const item = carouselItems[i];
      if (!item.messageTitle.trim()) return `카드 ${i + 1} 타이틀을 입력해주세요.`;
      if (!item.imageFile && !item.imageUploadedUrl) return `카드 ${i + 1} 이미지를 등록해주세요.`;
      if (!item.description.trim()) {
        return `카드 ${i + 1} 홍보문구를 입력해주세요.`;
      }
      if (messageType === "CAROUSEL_COMMERCE_MESSAGE") {
        const itemMobileLandingValidationMessage = validateUrlField(
          item.mobileLandingUrl,
          `카드 ${i + 1} 모바일 랜딩 URL을 입력해주세요.`,
          `카드 ${i + 1} 모바일 랜딩 URL은`
        );
        if (itemMobileLandingValidationMessage) return itemMobileLandingValidationMessage;
        const itemPcLandingValidationMessage = validateOptionalUrlField(
          item.pcLandingUrl,
          `카드 ${i + 1} PC 랜딩 URL은`
        );
        if (itemPcLandingValidationMessage) return itemPcLandingValidationMessage;
        if ((item.messageTitle.match(/\n/g) || []).length > 0) return `카드 ${i + 1} 타이틀은 개행 없이 입력해주세요.`;
        if (item.messageTitle.length > 25) return `카드 ${i + 1} 타이틀은 최대 25자까지 입력할 수 있습니다.`;
        if ((item.description.match(/\n/g) || []).length > 2) return `카드 ${i + 1} 홍보문구는 개행을 최대 2개까지 입력할 수 있습니다.`;
        if (item.description.length > 50) return `카드 ${i + 1} 홍보문구는 최대 50자까지 입력할 수 있습니다.`;
      }
      if (messageType === "CAROUSEL_FEED_MESSAGE") {
        if ((item.messageTitle.match(/\n/g) || []).length > 0) return `카드 ${i + 1} 타이틀은 개행 없이 입력해주세요.`;
        if (item.messageTitle.length > 20) return `카드 ${i + 1} 타이틀은 최대 20자까지 입력할 수 있습니다.`;
        if (item.description.length > 180) return `카드 ${i + 1} 홍보문구는 최대 180자까지 입력할 수 있습니다.`;
        if ((item.description.match(/\n/g) || []).length > 10) return `카드 ${i + 1} 홍보문구는 개행을 최대 10개까지 입력할 수 있습니다.`;
        if (!item.buttonTitle.trim()) return `카드 ${i + 1} 버튼1 이름을 입력해주세요.`;
        if (item.buttonTitle.length > 8) return `카드 ${i + 1} 버튼1 이름은 최대 8자까지 입력할 수 있습니다.`;
        const button1MobileLandingValidationMessage = validateUrlField(
          item.buttonMobileLandingUrl,
          `카드 ${i + 1} 버튼1 모바일 랜딩 URL을 입력해주세요.`,
          `카드 ${i + 1} 버튼1 모바일 랜딩 URL은`
        );
        if (button1MobileLandingValidationMessage) return button1MobileLandingValidationMessage;
        const button1PcLandingValidationMessage = validateOptionalUrlField(
          item.buttonPcLandingUrl,
          `카드 ${i + 1} 버튼1 PC 랜딩 URL은`
        );
        if (button1PcLandingValidationMessage) return button1PcLandingValidationMessage;
        if (item.secondaryButtonEnabled && !item.secondaryButtonTitle.trim()) {
          return `카드 ${i + 1} 버튼2 이름을 입력해주세요.`;
        }
        if (item.secondaryButtonEnabled && item.secondaryButtonTitle.length > 8) {
          return `카드 ${i + 1} 버튼2 이름은 최대 8자까지 입력할 수 있습니다.`;
        }
        if (item.secondaryButtonEnabled) {
          const button2MobileLandingValidationMessage = validateUrlField(
            item.secondaryButtonMobileLandingUrl,
            `카드 ${i + 1} 버튼2 모바일 랜딩 URL을 입력해주세요.`,
            `카드 ${i + 1} 버튼2 모바일 랜딩 URL은`
          );
          if (button2MobileLandingValidationMessage) return button2MobileLandingValidationMessage;
          const button2PcLandingValidationMessage = validateOptionalUrlField(
            item.secondaryButtonPcLandingUrl,
            `카드 ${i + 1} 버튼2 PC 랜딩 URL은`
          );
          if (button2PcLandingValidationMessage) return button2PcLandingValidationMessage;
        }
        if (item.couponEnabled) {
          const cardCouponMobileLandingValidationMessage = validateUrlField(
            item.coupon.mobileLandingUrl,
            `카드 ${i + 1} 쿠폰 모바일 랜딩 URL을 입력해주세요.`,
            `카드 ${i + 1} 쿠폰 모바일 랜딩 URL은`
          );
          if (cardCouponMobileLandingValidationMessage) return cardCouponMobileLandingValidationMessage;
          const cardCouponPcLandingValidationMessage = validateOptionalUrlField(
            item.coupon.pcLandingUrl,
            `카드 ${i + 1} 쿠폰 PC 랜딩 URL은`
          );
          if (cardCouponPcLandingValidationMessage) return cardCouponPcLandingValidationMessage;
        }
      }
      if (messageType === "CAROUSEL_COMMERCE_MESSAGE") {
        if (!item.priceAmount.trim()) return `카드 ${i + 1} 정가를 입력해주세요.`;
        if (!/^\d{1,8}$/.test(item.priceAmount.trim())) return `카드 ${i + 1} 정가는 8자리 이하 숫자만 입력할 수 있습니다.`;
        if (item.discountedPriceAmount && item.discountedPriceAmount.trim()) {
          if (!/^\d{1,8}$/.test(item.discountedPriceAmount.trim())) return `카드 ${i + 1} 할인가는 8자리 이하 숫자만 입력할 수 있습니다.`;
          const price = parseInt(item.priceAmount.trim(), 10);
          const discounted = parseInt(item.discountedPriceAmount.trim(), 10);
          if (discounted >= Math.floor(price * 0.99)) return `카드 ${i + 1} 할인가는 정가의 1% 이상 할인된 금액이어야 합니다.`;
        }
      }
    }
  }

  if (couponEnabled) {
    const titleLimit = getCouponTitleLimit(messageType);
    const descLimit = getCouponDescriptionLimit(messageType);
    const couponMobileLandingValidationMessage = validateUrlField(
      coupon.mobileLandingUrl,
      "쿠폰 모바일 랜딩 URL을 입력해주세요.",
      "쿠폰 모바일 랜딩 URL은"
    );
    if (couponMobileLandingValidationMessage) return couponMobileLandingValidationMessage;
    const couponPcLandingValidationMessage = validateOptionalUrlField(
      coupon.pcLandingUrl,
      "쿠폰 PC 랜딩 URL은"
    );
    if (couponPcLandingValidationMessage) return couponPcLandingValidationMessage;
    if (coupon.couponBookTitleType !== "FREE_SHIPPING" && !coupon.couponBookTitle.trim()) return "쿠폰 타이틀을 입력해주세요.";
    if (coupon.couponBookTitleType === "DISCOUNT_PRICE" && !/^\d{1,8}$/.test(coupon.couponBookTitle.trim())) {
      return "할인금액 쿠폰 타이틀은 8자리 이하 숫자만 입력할 수 있습니다.";
    }
    if (coupon.couponBookTitleType === "DISCOUNT_RATE" && !/^\d{1,2}$/.test(coupon.couponBookTitle.trim())) {
      return "할인율 쿠폰 타이틀은 2자리 이하 숫자만 입력할 수 있습니다.";
    }
    if (
      (coupon.couponBookTitleType === "FREE_GIFT" || coupon.couponBookTitleType === "UPGRADE") &&
      coupon.couponBookTitle.trim().length > titleLimit
    ) {
      return `쿠폰 타이틀은 최대 ${titleLimit}자까지 입력할 수 있습니다.`;
    }
    if (!coupon.title.trim()) return "쿠폰 상세 설명을 입력해주세요.";
    if (coupon.title.trim().length > descLimit) {
      return `쿠폰 상세 설명은 최대 ${descLimit}자까지 입력할 수 있습니다.`;
    }
  }

  return null;
}
